import { useQuery } from '@tanstack/react-query';
import { integrationsApi } from '@/services/api';

export interface AIOrbitModelOption {
  label: string;
  id: string;
}

/** "claude-sonnet-5-5" -> "Claude Sonnet 5.5" */
export function formatClaudeModelLabel(id: string): string {
  const parts = id.split('-').filter(Boolean);
  const words: string[] = [];
  let version: string[] = [];
  const flush = () => {
    if (version.length) {
      words.push(version.join('.'));
      version = [];
    }
  };
  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      version.push(part);
    } else {
      flush();
      words.push(part.charAt(0).toUpperCase() + part.slice(1));
    }
  }
  flush();
  return words.join(' ');
}

/** Display label: "claude-sonnet-5-5" -> "Claude Sonnet 5.5"; other ids are shown as served. */
export function formatAIOrbitModelLabel(id: string): string {
  return id.startsWith('claude-') ? formatClaudeModelLabel(id) : id;
}

/**
 * Chat models served by AIOrbit, Claude first (from the backend: live, cached, then the
 * last synced copy, then a static fallback). Used for both chat and agent model pickers.
 */
export function useAIOrbitModels() {
  const query = useQuery({
    queryKey: ['aiorbit-models'],
    queryFn: () => integrationsApi.getAIOrbitModels().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });
  const options: AIOrbitModelOption[] = (query.data?.models ?? []).map((id) => ({
    id,
    label: formatAIOrbitModelLabel(id),
  }));
  return {
    options,
    defaultModel: query.data?.default,
    fallbackModel: query.data?.fallback,
    source: query.data?.source,
    isLoading: query.isLoading,
  };
}
